import { Box, Stack, Typography } from "@mui/material";
import { MprcContainer, MprcFormSubHeader, MprcStringToHtml } from "components";
import { MprcTimePicker } from "components/MprcTimePicker";
import { AutoCompleteDataModelDto, ManagedMeetingDto, MeetingInstructorConflitsResponse, MeetingType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserAutoComplete } from "views/foundation/user/components/UserAutoComplete";
import { EditMeetingMode } from "./EditMeeting";

////////////////////////////////////////////////////////////////////////////////

interface IData {
    meetingType: MeetingType;
    multipleClasses: boolean;
    startDate: Date | null;
    endDate: Date | null;
    bookAdditionalTimeBefore: string | null;
    bookAdditionalTimeAfter: string | null;
    instructors: AutoCompleteDataModelDto[];
    instructorConflits: MeetingInstructorConflitsResponse[];
    assistants: AutoCompleteDataModelDto[];
}
interface IPros {
    mode: EditMeetingMode;
    visible: boolean;
    viewData: IData;
    viewErrors: ApiProblemErrors | undefined;
    readonly?: boolean;

    onFormUpdate: (dto: Partial<ManagedMeetingDto>) => void;
    onFormValidation: (valid: boolean, hasError: boolean) => void;
    onError: (error: any) => void;
}

interface IValidationFields {
    instructors: boolean;
    assistants: boolean;
}

interface IPostValidation {
    instructors: string[] | undefined;
    assistants: string[] | undefined;
}

////////////////////////////////////////////////////////////////////////////////

function getDefaultValidate(dto: IData): IValidationFields {
    var obj = {
        instructors: dto.multipleClasses ? true : dto.instructors.length > 0,
        assistants: true,
    } as IValidationFields;

    return obj;
}

/////////////////////////////////////////////////////////////////////////////////

const EditMeetingInstructorBoxComponent: React.FC<IPros> = (props) => {
    const { mode, visible, viewData, onFormUpdate, onFormValidation, viewErrors, readonly = false } = props;

    const { t } = useTranslation("lms");

    const instructorRequired = (mode == "classroom" || !viewData.multipleClasses || viewData.startDate != null || viewData.endDate != null);

    const [formValidation, setFormValidation] = useState<IValidationFields>(
        getDefaultValidate(viewData)
    );

    const vErrors = viewErrors ?? {};
    const postValidation: IPostValidation = {
        instructors: vErrors["instructors"],
        assistants: vErrors["assistants"],
    };

    /////////////////////////////////////////////////////////////////////////////////

    ///
    const handleUpdateRecord = (name: keyof IData, v: any) => {
        var newData = { ...viewData, [name]: v };
        setFormValidation(getDefaultValidate(newData));
        onFormUpdate(newData);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        onFormValidation(
            Object.values(formValidation).filter((x) => x === false).length == 0,
            Object.values(postValidation).filter(x => x !== undefined).length > 0
        );
    }, [formValidation, viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const additionalTimeBefore = moment(`1970-1-1 ${viewData.bookAdditionalTimeBefore}`).toDate();
    const additionalTimeAfter = moment(`1970-1-1 ${viewData.bookAdditionalTimeAfter}`).toDate();

    return (
        <Box sx={{ display: visible ? "block" : "none" }}>
            <MprcContainer>
                <MprcContainer>
                    <UserAutoComplete
                        disabled={readonly}
                        required={instructorRequired}
                        label={t("Meeting.InstructorBox.InstructorLabel")}
                        selectedItems={viewData.instructors}
                        onChange={items => {
                            handleUpdateRecord("instructors", items);
                        }}
                        error={(!formValidation.instructors || !!postValidation.instructors)}
                        helperText={!!postValidation?.instructors
                            ? <MprcStringToHtml text={postValidation?.instructors} />
                            : (viewData.meetingType == MeetingType.Teams && t("Meeting.InstructorBox.InstructorTeamsHint"))}
                    />
                    {viewData.instructorConflits &&
                        <Typography variant="caption" color="warning.main">
                            {viewData.instructorConflits.map(item => {
                                const diffDays = Formaters.dateDiffDays(item.endDate, item.startDate) ?? 0;
                                return <>
                                    <span key={item.instructorId}>
                                        {t("Meeting.InstructorBox.ConflictTimeHint", {
                                            name: item.name,
                                            start: diffDays < 1 ? Formaters.localeTime(item.startDate) : Formaters.performaDate(item.startDate, { preposition: "none", showTimePreposition: false }),
                                            end: diffDays < 1 ? Formaters.localeTime(item.endDate) : Formaters.performaDate(item.endDate, { preposition: "none", showTimePreposition: false }),
                                        })}
                                    </span>
                                    <br />
                                </>;
                            }
                            )}
                        </Typography>
                    }
                </MprcContainer>
                <MprcContainer>
                    <UserAutoComplete
                        disabled={readonly}
                        label={t("Meeting.InstructorBox.AssistantsLabel")}
                        selectedItems={viewData.assistants}
                        onChange={items => handleUpdateRecord("assistants", items)}
                        error={!formValidation.assistants || !!postValidation.assistants}
                        helperText={viewData.assistants != undefined && <MprcStringToHtml text={postValidation?.assistants} />}
                    />
                </MprcContainer>
            </MprcContainer>
            <MprcContainer>
                <MprcFormSubHeader
                    title={t("Meeting.InstructorBox.BookAdditionalTimeSubtitle")}
                    subtitle={t("Meeting.InstructorBox.BookAdditionalTimeHint")}
                />
                <Stack direction="row" spacing={2}>
                    <MprcTimePicker
                        disabled={readonly}
                        label={t("Meeting.InstructorBox.AdditionalTimeBeforeLabel")}
                        value={viewData.bookAdditionalTimeBefore ? additionalTimeBefore : null}
                        onChange={(value) => handleUpdateRecord("bookAdditionalTimeBefore", value == null ? null : moment(value).format("HH:mm"))}
                    />
                    <MprcTimePicker
                        disabled={readonly}
                        label={t("Meeting.InstructorBox.AdditionalTimeAfterLabel")}
                        value={viewData.bookAdditionalTimeAfter ? additionalTimeAfter : null}
                        onChange={(value) => handleUpdateRecord("bookAdditionalTimeAfter", value == null ? null : moment(value).format("HH:mm"))}
                    />
                </Stack>
            </MprcContainer>
        </Box>
    );
};

export const EditMeetingInstructorBox = React.memo(EditMeetingInstructorBoxComponent, (prev, next) => {
    return prev.mode == next.mode
        && prev.visible == next.visible
        && prev.readonly == next.readonly
        && LibUtils.deepCompare(prev.viewData, next.viewData)
        ;
});
